import type { ColumnDef, TableMeta, SortingState, Row, Table, Cell } from '@tanstack/react-table';
import { IndeterminateCheckbox } from 'components/inputs/IndeterminateCheckbox';
import { type TableConfig, TableFilter, TableMetaProps, TableState } from 'table/types';
import { TypeId } from '../components/Table';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { Box } from '@mui/material';
import { colors } from 'styles/theme/colors';
import { useGet } from 'lib/api';
import type { ComboBoxOption, ComboBoxApi } from 'components/inputs/types';

/* eslint-disable no-barrel-files/no-barrel-files -- intentional transitional compat barrel for existing import sites; ./fillerColumnOrder is canonical (see fe-architecture.md §4: existing barrels may remain until refactored) */
export {
  TABLE_EDGE_SCROLL_GUTTER_WIDTH,
  FILLER_COLUMN_ID,
  SELECTOR_COLUMN_ID,
  withFillerColumnLast,
  normalizeColumnOrder,
  resolveSelectableColumnOrder,
  buildAllLeafColumnIds,
  columnOrdersEqual,
  createFillerColumn,
  tableHasSelectorColumn,
} from './fillerColumnOrder';
/* eslint-enable no-barrel-files/no-barrel-files -- end intentional compat barrel re-export */

export const getTableMeta = <T,>(
  setData: React.Dispatch<React.SetStateAction<T[]>>,
  metaProps?: TableMetaProps<T>,
  selectedRows?: string[]
) => {
  return {
    updateData: (rowIndex: number, tableUpdate: Record<string, unknown>) => {
      setData(old =>
        old.map((row, index) => {
          if (index !== rowIndex) return row;

          return {
            ...old[rowIndex],
            ...tableUpdate,
          };
        })
      );
    },
    ...(metaProps ? metaProps : {}),
    ...(metaProps?.updateData && selectedRows
      ? {
          updateData: (
            rowIndex: number,
            tableUpdate: Record<string, unknown>,
            apiUpdate?: Record<string, unknown>,
            isLinked?: boolean
          ) => metaProps.updateData?.(selectedRows, rowIndex, tableUpdate, apiUpdate, isLinked),
        }
      : {}),
    // Merged meta: base 2-arg updateData and optional bulk override share AppTableMetaUpdateData overloads.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Structural merge cannot satisfy overloaded TableMeta.updateData without this cast.
  } as unknown as TableMeta<T>;
};

export const renderIndentation = (depth: number) =>
  Array(depth)
    .fill(null)
    .map((_, i) => (
      <Box
        key={`indent-${i + 1}`}
        sx={{
          width: '72px',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 24px',
          height: '100%',
        }}
      />
    ));

// horizontal padding for the select cell — the same 12px every other table cell uses.
const SELECT_CELL_PADDING_X = 12;
/** the checkbox's own hit area. */
const CHECKBOX_SIZE = 36;
// a checkbox-only column is exactly its padding + the box, so there is no dead space
const SELECT_COLUMN_WIDTH = SELECT_CELL_PADDING_X * 2 + CHECKBOX_SIZE;
const EXPANDABLE_COLUMN_WIDTH = 72;

export const createSelectorColumn = <T extends TypeId>(
  isSelectable?: boolean,
  isExpandable?: boolean,
  subRowsDepth = 1,
  v1 = false,
  configEvent?: TableConfig['event']
): ColumnDef<T>[] => {
  if (!isExpandable && !isSelectable) {
    return [];
  }
  const checkboxCell = (row: Row<T>) => (
    <Box
      sx={{
        padding: `8px ${SELECT_CELL_PADDING_X}px`,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <IndeterminateCheckbox
        disableRipple
        disabled={row.original.id === -1}
        checked={row?.getIsSelected()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
        size={v1 ? 'medium' : 'small'}
      />
    </Box>
  );

  const renderToggleIcon = (row: Row<T>) => {
    const Icon = row.getIsExpanded() ? CaretDown : CaretRight;
    return (
      <Box
        sx={{
          padding: '8px 24px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon
          size={16}
          color={colors.grey[700]}
          onClick={row.getToggleExpandedHandler()}
          style={{ cursor: 'pointer', minWidth: '16px' }}
        />
      </Box>
    );
  };

  const depth = isSelectable ? subRowsDepth : subRowsDepth - 1 || 1;
  const columnWidth = (isExpandable ? EXPANDABLE_COLUMN_WIDTH : SELECT_COLUMN_WIDTH) * depth;

  return [
    {
      id: 'Selector',
      enableSorting: false,
      maxSize: columnWidth,
      minSize: columnWidth,
      size: columnWidth,
      enableResizing: false,
      header: ({ table }) =>
        isSelectable ? (
          <IndeterminateCheckbox
            disableRipple
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={e => {
              const action = table.getIsAllRowsSelected() ? 'Deselection' : 'Selection';
              configEvent?.('selectAll', { action });
              table.getToggleAllRowsSelectedHandler()(e);
            }}
            size={v1 ? 'medium' : 'small'}
          />
        ) : (
          ''
        ),
      cell: ({ row }) => {
        if (row.original.id === -1) return null;
        if (isExpandable) {
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {renderIndentation(row.depth ?? 0)}
              {isSelectable && checkboxCell(row)}
              {row.getCanExpand() ? renderToggleIcon(row) : null}
            </Box>
          );
        }
        return checkboxCell(row);
      },
    },
  ];
};

export const updateSorting = (sortingState: SortingState, tableState?: TableState) => {
  const oldSort = tableState?.sort ?? null;
  const oldOrder = tableState?.order;

  const newSort = sortingState[0]?.id ?? oldSort;
  const isSameSort = newSort === oldSort;

  const order = isSameSort
    ? oldOrder === 'ASC'
      ? 'DESC'
      : oldOrder === 'DESC'
        ? null
        : 'ASC'
    : 'ASC';

  const sort = newSort && order ? newSort : null;

  return { sort, order: sort ? order : null };
};

export const rowHeight = 64;

export const getIsFilterChange = (
  obj1: Record<string, string | null>,
  obj2: Record<string, string | null>,
  allFilters: TableFilter[]
): boolean => {
  for (const key in obj1) {
    const isKeyInFilter = allFilters.some(item => item?.id === key);
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (isKeyInFilter && String(val1) !== String(val2)) {
      return true;
    }
  }
  return false;
};

export function getTotalHeadersSize<T>(table?: Table<T>): number {
  if (!table) return 0;
  return table?.getHeaderGroups()?.reduce((total, headerGroup) => {
    return (
      total +
      headerGroup?.headers?.reduce((groupTotal, header) => {
        const isFrozen = header.column.getIsPinned();
        if (isFrozen) {
          return groupTotal + 0;
        }
        return groupTotal + header?.getSize();
      }, 0)
    );
  }, 0);
}

export function isCellEditable<T>(
  editable?: boolean | ((value?: Cell<T, unknown>) => boolean),
  cell?: Cell<T, unknown>
): boolean {
  if (typeof editable === 'function') {
    return editable(cell); // Call the function with the cell context
  }

  return !!editable; // Return the boolean directly
}

// Map Sort/Order keys to lowercase for URL parameters
export const mapUrlKeys = (obj: Record<string, unknown>) => {
  const mapped: Record<string, unknown> = {};
  Object.keys(obj).forEach(key => {
    if (key === 'Sort') {
      mapped.sort = obj[key];
    } else if (key === 'Order') {
      mapped.order = obj[key];
    } else {
      mapped[key] = obj[key];
    }
  });
  return mapped;
};

/**
 * Hook to fetch and transform API options for ComboBox components
 * Reuses React Query cache if data is already fetched
 * @param api - ComboBoxApi configuration
 * @param staticOptions - Static options array (used when api is not provided)
 * @returns Transformed options array with customLabel and showCustomLabel
 */

export const useComboBoxOptions = (
  api: ComboBoxApi | null | undefined,
  staticOptions?: ComboBoxOption[]
): ComboBoxOption[] => {
  // Helper to resolve vars (can be array or function)
  const getVars = () => {
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  };

  const { data: apiData } = useGet(
    api?.url || null,
    api?.search ? [api?.key, ''] : api?.key || '',
    api?.search ? ['', ...getVars()] : [...getVars()],
    undefined,
    api?.revalidate !== false,
    { enabled: !!api }
  );

  if (apiData && api) {
    return api.responseTransform(apiData);
  }

  return staticOptions || [];
};
